import React from "react";
import Tooltip from "@/components/Tooltip";
import { EyeSVG } from "@/assets/svg";

type EmailItem = {
  id: string;
  client: {
    name: string;
  };
  project: {
    name: string;
  };
  scenario: string;
  sent_at: string;
  status: "sent" | "failed" | "active";
};

type EmailTableProps = {
  rows: EmailItem[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPrev: () => void;
  onNext: () => void;
  onView?: (row: EmailItem) => void;
};

const StatusBadge: React.FC<{ status: EmailItem["status"] }> = ({ status }) => {
  const styles =
    status === "sent"
      ? "bg-[#EDFFF9] text-[#0C7453]"
      : status === "failed"
        ? "bg-[#FFEDED] text-[#FF0000]"
        : "bg-[#DCF1FE] text-[#007FFF]";
  return (
    <span className={`inline-block px-2 py-1 rounded-sm text-xs font-medium ${styles}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const EmailTable: React.FC<EmailTableProps> = ({
  rows,
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPrev,
  onNext,
  onView,
}) => {
  return (
    <>
      <div className="overflow-x-auto relative py-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#F4F7F9] text-left">
            <tr>
              <th
                className="p-3 text-sm font-[600] text-[#1E1E1E]"
                style={{ width: "236px", minWidth: "236px", maxWidth: "236px" }}
              >
                Client name
              </th>
              <th
                className="p-3 text-sm font-[600] text-[#1E1E1E]"
                style={{ width: "178px", minWidth: "178px", maxWidth: "178px" }}
              >
                Project
              </th>
              <th
                className="p-3 text-sm font-[600] text-[#1E1E1E]"
                style={{ width: "178px", minWidth: "178px", maxWidth: "178px" }}
              >
                Scenario
              </th>
              <th
                className="p-3 text-sm font-[600] text-[#1E1E1E]"
                style={{ width: "200px", minWidth: "200px", maxWidth: "200px" }}
              >
                Sent Date & Time
              </th>
              <th
                className="p-3 text-sm font-[600] text-[#1E1E1E]"
                style={{ width: "120px", minWidth: "120px", maxWidth: "120px" }}
              >
                Status
              </th>
              <th
                className="p-3 text-sm font-[600] text-[#1E1E1E]"
                style={{ width: "78px", minWidth: "78px", maxWidth: "78px" }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  No emails found
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-[#F9FEFF]">
                  <td
                    className="whitespace-nowrap text-sm text-[#1E1E1E] font-[500] px-3 py-4"
                    title={row.client.name}
                    style={{ width: "236px", minWidth: "236px", maxWidth: "236px" }}
                  >
                    {row.client.name}
                  </td>
                  <td
                    className="whitespace-nowrap text-sm text-[#1E1E1E] font-[500] px-3 py-4"
                    title={row?.project?.name}
                    style={{ width: "178px", minWidth: "178px", maxWidth: "178px" }}
                  >
                    {row?.project?.name}
                  </td>
                  <td
                    className="whitespace-nowrap text-sm text-[#1E1E1E] font-[500] px-3 py-4"
                    title={row.scenario}
                    style={{ width: "178px", minWidth: "178px", maxWidth: "178px" }}
                  >
                    {row.scenario.charAt(0).toUpperCase() + row.scenario.slice(1)}
                  </td>
                  <td
                    className="whitespace-nowrap text-sm text-[#1E1E1E] font-[500] px-3 py-4"
                    style={{ width: "200px", minWidth: "200px", maxWidth: "200px" }}
                  >
                    {row.sent_at}
                  </td>
                  <td
                    className="whitespace-nowrap text-sm text-[#1E1E1E] font-[500] px-3 py-4"
                    style={{ width: "120px", minWidth: "120px", maxWidth: "120px" }}
                  >
                    <StatusBadge status={row.status} />
                  </td>
                  <td
                    className="whitespace-nowrap text-sm text-[#1E1E1E] font-[500] px-3 py-4"
                    style={{ width: "78px", minWidth: "78px", maxWidth: "78px" }}
                  >
                    <div className="flex items-center space-x-2.5">
                      <Tooltip content="View">
                        <button className="cursor-pointer" aria-label="View email" onClick={() => onView?.(row)}>
                          <EyeSVG />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-4 text-sm text-[#1E1E1E] font-[600] flex justify-between items-center">
        <div>
          Showing {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} results
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={onPrev}
            disabled={currentPage <= 1}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Previous
          </button>
          <button
            onClick={onNext}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default EmailTable;
